import { HttpClient } from '@/components/shared/http-client'
import { differenceInDays, parseISO } from 'date-fns'

const setGlobalAlert = (context, payload) => {
  context.commit('SET_GLOBAL_ALERT', payload)
}

const setGlobalErrorAlert = (context, payload) => {
  context.commit('SET_GLOBAL_ERROR_ALERT', payload)
}

const setGlobalInfo = (context, payload) => {
  context.commit('SET_GLOBAL_INFO', payload)
}

const setLoading = (context, payload) => {
  context.commit('SET_LOADING', payload)
}

const setUser = (context, payload) => {
  context.commit('SET_USER', payload)
}

const setRedirectUrl = (context, payload) => {
  context.commit('SET_REDIRECT_URL', payload)
}

// This is called on login, or on account selection
// or page refresh
const setActiveAccount = async (context, payload) => {
  if (payload && payload.accountCode) {
    const accountCode = payload.accountCode
    const getAccountPermissionsUrl = `/api/users/me/${accountCode}/permissions`
    const response = await HttpClient.get(getAccountPermissionsUrl)
    if (response && response.data && response.data.permissions) {
      payload.permissions = response.data.permissions
    }
  }

  context.commit('SET_ACTIVE_ACCOUNT', payload)
}

const setAccountTasks = async (context, payload) => {
  await context.commit('SET_ACCOUNT_TASKS', payload)
}

// Clears cookies and resets vuex for logout
const logout = async context => {
  const intervalName = context.getters.getIntervalName
  if (intervalName) clearInterval(intervalName)

  // Needed to clear http-only cookies
  await HttpClient.post('/auth/logout')

  context.dispatch('setUser', {})
  context.commit('SET_ACCOUNT_ROLES', {})
  context.commit('SET_BROKER_ROLES', {})
  context.commit('SET_BROKERAGES', {})
  context.commit('SET_LOADING', false)
  context.dispatch('clearActiveAccount')
}

const clearActiveAccount = async context => {
  await context.dispatch('setActiveAccount', {})
  context.commit('SET_FEATURES_LOADED', false)
  context.commit('SET_FEATURE_FLAGS', {})
  context.commit('SET_PREFERENCES', [])
}

const initializeAccountNotifications = async context => {
  const { activeAccount } = context.state
  if (activeAccount && activeAccount.accountCode) {
    let accountNotifications = JSON.parse(localStorage.getItem(`account-notifications-${activeAccount.accountCode}`)) || []
    const now = new Date()
    accountNotifications = accountNotifications.filter(notification => {
      if (!notification.time) return true
      const notificationDate = parseISO(notification.time)
      return differenceInDays(now, notificationDate) <= 7
    })
    context.commit('SET_ACCOUNT_NOTIFICATIONS', accountNotifications)
  } else {
    context.commit('SET_ACCOUNT_NOTIFICATIONS', [])
  }
}

const clearAccountNotifications = async context => {
  await context.commit('CLEAR_ALL_ACCOUNT_NOTIFICATIONS')
}

// Payload Type: { details, notes?, link? }
const addAccountNotification = async (context, payload) => {
  await context.dispatch('setGlobalInfo', {
    show: true,
    text: payload.details,
  })
  await context.commit('ADD_ACCOUNT_NOTIFICATION', payload)
}

const removeAccountNotification = async (context, payload) => {
  await context.commit('REMOVE_ACCOUNT_NOTIFICATION', payload)
}

// Reads the token and sets some global information
// 2 param matches the '/me' payload
const newLogin = async (
  context,
  {
    userId,
    username,
    displayUsername,
    firstName,
    lastName,
    isAnAdmin,
    isViewer,
    hasMultipleAccounts,
    accountRoles,
    brokerages,
    brokerRoles,
    tfaEnabled,
    tfaPhone,
    tfaType,
    tfaValidated,
    email,
    publicPhone,
    profilePicture,
    licensesOrDesignations,
    titles,
    services,
    userAccounts,
  }
) => {
  await context.dispatch('setUser', {
    userId: userId,
    username: username,
    displayUsername: displayUsername,
    firstName: firstName,
    lastName: lastName,
    email: email,
    hasMultipleAccounts: hasMultipleAccounts,
    isAdmin: isAnAdmin,
    isViewer: isViewer,
    tfaEnabled: tfaEnabled,
    tfaValidated: tfaValidated,
    tfaPhone: tfaPhone,
    tfaType: tfaType,
    userAccounts: userAccounts,
    publicPhone,
    profilePicture,
    licensesOrDesignations,
    titles,
    services,
  })

  if (accountRoles) {
    context.commit('SET_ACCOUNT_ROLES', accountRoles)

    const accts = Object.keys(accountRoles)
    if (!isAnAdmin && accts.length === 1) {
      await context.dispatch('setActiveAccount', {
        accountCode: accts[0],
      })
    }
  }

  if (brokerRoles) {
    context.commit('SET_BROKER_ROLES', brokerRoles)
  }
  if (brokerages) {
    context.commit('SET_BROKERAGES', brokerages)
  }
}

const setRefreshTokenIntervalName = (context, intervalName) => {
  context.commit('SET_REFRESH_INTERVAL_NAME', intervalName)
}

const setDashboardAppCycle = (context, payload) => {
  context.commit('SET_DASHBOARD', { applicationCycle: payload })
}

const setFeatureFlags = (context, payload) => {
  context.commit('SET_FEATURE_FLAGS', payload)
  context.commit('SET_FEATURES_LOADED', true)
}

const setPreferences = (context, payload) => {
  context.commit('SET_PREFERENCES', payload)
}

const updatePreference = async (context, payload) => {
  context.commit('UPDATE_PREFERENCE', payload)
}

// Admin viewing a client account
const setAdminAccountView = (context, { accountCode, companyName, linkedAgents }) => {
  context.dispatch('setActiveAccount', {
    accountCode,
    companyName,
    linkedAgents,
  })
}

const setCodeSplitRefreshNotice = (context, payload) => {
  context.commit('SET_CODE_SPLIT_REFRESH_NOTICE', payload)
}

const setApplicationModel = (context, payload) => {
  context.commit('SET_APPLICATION_MODEL', payload)
}

export default {
  setGlobalErrorAlert,
  setGlobalAlert,
  setGlobalInfo,
  setLoading,
  setUser,
  logout,
  setActiveAccount,
  clearActiveAccount,
  setAdminAccountView,
  setDashboardAppCycle,
  setFeatureFlags,
  setPreferences,
  updatePreference,
  setCodeSplitRefreshNotice,
  newLogin,
  setRefreshTokenIntervalName,
  setAccountTasks,
  setApplicationModel,
  initializeAccountNotifications,
  clearAccountNotifications,
  addAccountNotification,
  removeAccountNotification,
  setRedirectUrl,
}
